import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  Alert,
  Spinner,
  Container,
  Card,
  Row,
  Col,
  Accordion,
} from 'react-bootstrap';
import Header from '../../common/Header';
import './Settings.css';

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [settings, setSettings] = useState({
    whatsappEnabled: true,
    emailEnabled: true,
    signatureOptions: {
      text: true,
      canvas: true,
      upload: true,
    },
    saveTrigger: 'create_page',
    messageTemplates: {
      whatsapp:
        '📄 مرحباً {name}، مرفق لكم عقد التسجيل. يرجى المراجعة والتوقيع.',
      email: {
        subject: 'عقد تسجيل الطالب - مدارس إثراء',
        body: 'السلام عليكم {name},\n\nمرفق لكم عقد تسجيل الطالب في مدارس إثراء.\nيرجى المراجعة والتوقيع في أقرب وقت ممكن.\n\nشكراً لكم,\nإدارة مدارس إثراء',
      },
    },
  });

  // Fetch settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/settings`,
        {
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setSettings(data.data);
      } else {
        setError(data.message || 'فشل في جلب الإعدادات');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setError('حدث خطأ في جلب الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/settings`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(settings),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess('تم حفظ الإعدادات بنجاح!');
        setSettings(data.data);
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError('حدث خطأ في حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <>
        <Header />
        <Container className="py-4 text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </Spinner>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="py-4" dir="rtl">
        <Card className="shadow-sm">
          <div
            className="text-white p-3"
            style={{
              backgroundColor: '#073d30',
              borderTopLeftRadius: '0.375rem',
              borderTopRightRadius: '0.375rem',
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="light"
                onClick={handleBack}
                className="d-flex align-items-center"
              >
                رجوع
              </Button>
              <h3 className="mb-0 text-center flex-grow-1">إعدادات النظام</h3>
              <div style={{ width: '60px' }}></div> {/* Spacer for balance */}
            </div>
          </div>
          <Card.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Accordion defaultActiveKey={['0', '1', '2', '3']} alwaysOpen>
                {/* خيارات التواصل */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>خيارات التواصل</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          id="whatsapp-switch"
                          label="تفعيل إرسال واتساب"
                          checked={settings.whatsappEnabled}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              whatsappEnabled: e.target.checked,
                            })
                          }
                          reverse
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="switch"
                          id="email-switch"
                          label="تفعيل إرسال البريد الإلكتروني"
                          checked={settings.emailEnabled}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              emailEnabled: e.target.checked,
                            })
                          }
                          reverse
                        />
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* خيارات التوقيع */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>خيارات التوقيع</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-2">
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="sig-text"
                          label="توقيع بالاسم (نص)"
                          checked={settings.signatureOptions.text}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              signatureOptions: {
                                ...settings.signatureOptions,
                                text: e.target.checked,
                              },
                            })
                          }
                          reverse
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="sig-canvas"
                          label="توقيع بالرسم"
                          checked={settings.signatureOptions.canvas}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              signatureOptions: {
                                ...settings.signatureOptions,
                                canvas: e.target.checked,
                              },
                            })
                          }
                          reverse
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Check
                          type="checkbox"
                          id="sig-upload"
                          label="رفع صورة توقيع"
                          checked={settings.signatureOptions.upload}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              signatureOptions: {
                                ...settings.signatureOptions,
                                upload: e.target.checked,
                              },
                            })
                          }
                          reverse
                        />
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* خيارات الحفظ */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>خيارات الحفظ</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-2">
                      <Col>
                        <Form.Label className="d-block mb-2">
                          متى يتم حفظ العقد؟
                        </Form.Label>
                        <Form.Check
                          type="radio"
                          name="saveTrigger"
                          id="save-create"
                          label="قبل التوقيع والمعاينه في صفحه الإنشاء"
                          checked={settings.saveTrigger === 'create_page'}
                          onChange={() =>
                            setSettings({
                              ...settings,
                              saveTrigger: 'create_page',
                            })
                          }
                          className="mb-2"
                          reverse
                        />
                        <Form.Check
                          type="radio"
                          name="saveTrigger"
                          id="save-signature"
                          label="بعد التوقيع والمعاينه في صفحه التوقيع"
                          checked={settings.saveTrigger === 'signature_page'}
                          onChange={() =>
                            setSettings({
                              ...settings,
                              saveTrigger: 'signature_page',
                            })
                          }
                          reverse
                        />
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* قوالب الرسائل */}
                <Accordion.Item eventKey="3">
                  <Accordion.Header>قوالب الرسائل</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-2">
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>قالب رسالة واتساب</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            dir="rtl"
                            value={settings.messageTemplates?.whatsapp || ''}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                messageTemplates: {
                                  ...settings.messageTemplates,
                                  whatsapp: e.target.value,
                                },
                              })
                            }
                            placeholder="استخدم {name} لإدراج اسم ولي الأمر"
                          />
                          <Form.Text className="text-muted">
                            مثال: 📄 مرحباً {'{name}'}، مرفق لكم عقد التسجيل.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>موضوع البريد الإلكتروني</Form.Label>
                          <Form.Control
                            type="text"
                            dir="rtl"
                            value={
                              settings.messageTemplates?.email?.subject || ''
                            }
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                messageTemplates: {
                                  ...settings.messageTemplates,
                                  email: {
                                    ...settings.messageTemplates?.email,
                                    subject: e.target.value,
                                  },
                                },
                              })
                            }
                            placeholder="عقد تسجيل الطالب - مدارس إثراء"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>نص البريد الإلكتروني</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={5}
                            dir="rtl"
                            value={settings.messageTemplates?.email?.body || ''}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                messageTemplates: {
                                  ...settings.messageTemplates,
                                  email: {
                                    ...settings.messageTemplates?.email,
                                    body: e.target.value,
                                  },
                                },
                              })
                            }
                            placeholder="استخدم {name} لإدراج اسم ولي الأمر"
                          />
                          <Form.Text className="text-muted">
                            مثال: السلام عليكم {'{name}'}، مرفق لكم عقد تسجيل
                            الطالب.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="d-flex justify-content-end mt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      جاري الحفظ...
                    </>
                  ) : (
                    'حفظ التغييرات'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default Settings;
